import React, { useState, useEffect, useCallback } from "react";
import { Link } from "react-router-dom";
import { useAuth } from "../../context/AuthContext";

const RUOLI = [
  { value: 100000000, label: "Admin" },
  { value: 100000001, label: "RSPP" },
  { value: 100000002, label: "Direzione" },
  { value: 100000003, label: "Direttore RSA" },
  { value: 100000004, label: "Amministrativo" },
  { value: 100000005, label: "Fornitore" },
  { value: 100000006, label: "Stakeholder" },
];

function UtentiPage() {
  const { apiRequest, user: currentUser } = useAuth();
  const [utenti, setUtenti] = useState([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  const [stats, setStats] = useState(null);

  // Filters
  const [search, setSearch] = useState("");
  const [ruoloFilter, setRuoloFilter] = useState("");
  const [attivoFilter, setAttivoFilter] = useState("");

  // Modal state
  const [showModal, setShowModal] = useState(false);
  const [modalMode, setModalMode] = useState("create"); // create, edit, view
  const [selectedUtente, setSelectedUtente] = useState(null);
  const [formData, setFormData] = useState({
    displayName: "",
    email: "",
    ruolo: "",
    rsaId: "",
    fornitoreId: "",
    note: "",
  });
  const [formError, setFormError] = useState(null);
  const [submitting, setSubmitting] = useState(false);

  // RSA and Fornitori for dropdowns
  const [rsaList, setRsaList] = useState([]);
  const [fornitoriList, setFornitoriList] = useState([]);

  // CIAM status
  const [ciamConfigured, setCiamConfigured] = useState(false);
  const [resendingInvite, setResendingInvite] = useState(null);

  // Fetch RSA, Fornitori and CIAM status once
  useEffect(() => {
    const fetchOptions = async () => {
      try {
        const [rsaRes, fornitoriRes, ciamRes] = await Promise.all([
          apiRequest("/rsa"),
          apiRequest("/fornitori"),
          apiRequest("/utenti/ciam-status").catch(() => ({
            configured: false,
          })),
        ]);
        setRsaList(rsaRes.value || []);
        setFornitoriList(fornitoriRes.value || []);
        setCiamConfigured(ciamRes.configured);
      } catch (err) {
        console.error("Error fetching options:", err);
      }
    };
    fetchOptions();
  }, [apiRequest]);

  // Fetch users
  const fetchUtenti = useCallback(async () => {
    setLoading(true);
    setError(null);
    try {
      const params = new URLSearchParams();
      if (search) params.append("search", search);
      if (ruoloFilter) params.append("ruolo", ruoloFilter);
      if (attivoFilter) params.append("attivo", attivoFilter);

      const result = await apiRequest(`/utenti?${params.toString()}`);
      setUtenti(result.value || []);
    } catch (err) {
      setError("Errore nel caricamento degli utenti");
      console.error("Error fetching utenti:", err);
    } finally {
      setLoading(false);
    }
  }, [apiRequest, search, ruoloFilter, attivoFilter]);

  // Fetch stats
  const fetchStats = useCallback(async () => {
    try {
      const result = await apiRequest("/utenti/stats/overview");
      setStats(result);
    } catch (err) {
      console.error("Error fetching stats:", err);
    }
  }, [apiRequest]);

  useEffect(() => {
    const debounce = setTimeout(fetchUtenti, 300);
    return () => clearTimeout(debounce);
  }, [fetchUtenti]);

  useEffect(() => {
    fetchStats();
  }, [fetchStats]);

  // Open modal
  const openCreateModal = () => {
    setModalMode("create");
    setSelectedUtente(null);
    setFormData({
      displayName: "",
      email: "",
      ruolo: "",
      rsaId: "",
      fornitoreId: "",
      note: "",
    });
    setFormError(null);
    setShowModal(true);
  };

  const openEditModal = (utente) => {
    setModalMode("edit");
    setSelectedUtente(utente);
    setFormData({
      displayName: utente.displayName,
      email: utente.email,
      ruolo: utente.ruolo,
      rsaId: utente.rsa?.id || "",
      fornitoreId: utente.fornitore?.id || "",
      note: utente.note || "",
    });
    setFormError(null);
    setShowModal(true);
  };

  // Handle form submit
  const handleSubmit = async (e) => {
    e.preventDefault();
    setFormError(null);
    setSubmitting(true);

    try {
      const payload = {
        displayName: formData.displayName,
        email: formData.email,
        ruolo: parseInt(formData.ruolo),
        rsaId: formData.rsaId || null,
        fornitoreId: formData.fornitoreId || null,
        note: formData.note || null,
      };

      if (modalMode === "create") {
        const result = await apiRequest("/utenti", {
          method: "POST",
          body: JSON.stringify(payload),
        });

        // Show invitation status
        if (result.invitation?.success) {
          alert(`Utente creato! Email di invito inviata a ${payload.email}`);
        } else if (result.invitation?.skipped) {
          alert(
            `Utente creato, ma l'invito Azure non è stato inviato (CIAM non configurato).`,
          );
        } else if (result.invitation?.error) {
          alert(
            `Utente creato, ma errore invio invito: ${result.invitation.error}`,
          );
        }
      } else {
        await apiRequest(`/utenti/${selectedUtente.id}`, {
          method: "PUT",
          body: JSON.stringify(payload),
        });
      }

      setShowModal(false);
      fetchUtenti();
      fetchStats();
    } catch (err) {
      setFormError(err.message || "Errore durante il salvataggio");
    } finally {
      setSubmitting(false);
    }
  };

  // Toggle active status
  const toggleAttivo = async (utente) => {
    if (utente.id === currentUser?.id) {
      alert("Non puoi disattivare il tuo stesso account");
      return;
    }

    try {
      await apiRequest(`/utenti/${utente.id}/attiva`, {
        method: "PATCH",
        body: JSON.stringify({ attivo: !utente.attivo }),
      });
      fetchUtenti();
      fetchStats();
    } catch (err) {
      console.error("Error toggling user status:", err);
      alert("Errore durante l'aggiornamento dello stato");
    }
  };

  // Delete user
  const deleteUtente = async (utente) => {
    if (utente.id === currentUser?.id) {
      alert("Non puoi eliminare il tuo stesso account");
      return;
    }

    if (
      !confirm(
        `Sei sicuro di voler disattivare l'utente "${utente.displayName}"?`,
      )
    ) {
      return;
    }

    try {
      await apiRequest(`/utenti/${utente.id}`, {
        method: "DELETE",
      });
      fetchUtenti();
      fetchStats();
    } catch (err) {
      console.error("Error deleting user:", err);
      alert("Errore durante l'eliminazione");
    }
  };

  // Resend invitation
  const resendInvite = async (utente) => {
    if (!ciamConfigured) {
      alert("Azure CIAM non configurato");
      return;
    }

    setResendingInvite(utente.id);
    try {
      await apiRequest(`/utenti/${utente.id}/reinvia-invito`, {
        method: "POST",
      });
      alert(`Invito reinviato a ${utente.email}`);
      fetchUtenti();
    } catch (err) {
      console.error("Error resending invite:", err);
      alert(`Errore: ${err.message}`);
    } finally {
      setResendingInvite(null);
    }
  };

  if (loading && utenti.length === 0) {
    return (
      <div className="flex items-center justify-center h-64">
        <div className="spinner w-10 h-10"></div>
      </div>
    );
  }

  return (
    <div className="p-6 space-y-6">
      {/* CIAM Warning */}
      {!ciamConfigured && (
        <div className="bg-yellow-50 border border-yellow-200 rounded-lg p-4 flex items-start gap-3">
          <span className="text-yellow-600 text-xl">⚠️</span>
          <div>
            <p className="font-medium text-yellow-800">
              Azure CIAM non configurato
            </p>
            <p className="text-sm text-yellow-700">
              Gli utenti creati non riceveranno l'email di invito. Configura
              CIAM_CLIENT_SECRET nel backend per abilitare gli inviti
              automatici.
            </p>
          </div>
        </div>
      )}

      {/* Header */}
      <div className="flex justify-between items-center">
        <div>
          <h1 className="text-2xl font-bold text-gray-900">Gestione Utenti</h1>
          <p className="text-gray-500 mt-1">
            Amministrazione utenti del portale
          </p>
        </div>
        <button onClick={openCreateModal} className="btn-primary">
          + Nuovo Utente
        </button>
      </div>

      {/* Stats Cards */}
      {stats && (
        <div className="grid grid-cols-1 md:grid-cols-4 gap-4">
          <div className="bg-white rounded-lg border p-4">
            <div className="text-2xl font-bold text-primary-600">
              {stats.totaleAttivi}
            </div>
            <div className="text-sm text-gray-500">Utenti Attivi</div>
          </div>
          <div className="bg-white rounded-lg border p-4">
            <div className="text-2xl font-bold text-gray-400">
              {stats.totaleInattivi}
            </div>
            <div className="text-sm text-gray-500">Utenti Inattivi</div>
          </div>
          <div className="bg-white rounded-lg border p-4">
            <div className="text-2xl font-bold text-green-600">
              {stats.attiviUltimi7Giorni}
            </div>
            <div className="text-sm text-gray-500">Attivi (7 giorni)</div>
          </div>
          <div className="bg-white rounded-lg border p-4">
            <div className="flex gap-2 flex-wrap">
              {stats.perRuolo?.slice(0, 3).map((r) => (
                <span
                  key={r.ruoloId}
                  className="text-xs bg-gray-100 rounded px-2 py-1"
                >
                  {r.label}: {r.count}
                </span>
              ))}
            </div>
            <div className="text-sm text-gray-500 mt-2">Per Ruolo</div>
          </div>
        </div>
      )}

      {/* Filters */}
      <div className="card">
        <div className="flex gap-4 flex-wrap">
          <input
            type="text"
            placeholder="Cerca per nome o email..."
            value={search}
            onChange={(e) => setSearch(e.target.value)}
            className="input flex-1 min-w-[200px]"
          />
          <select
            value={ruoloFilter}
            onChange={(e) => setRuoloFilter(e.target.value)}
            className="input w-48"
          >
            <option value="">Tutti i ruoli</option>
            {RUOLI.map((r) => (
              <option key={r.value} value={r.value}>
                {r.label}
              </option>
            ))}
          </select>
          <select
            value={attivoFilter}
            onChange={(e) => setAttivoFilter(e.target.value)}
            className="input w-40"
          >
            <option value="">Tutti</option>
            <option value="true">Attivi</option>
            <option value="false">Inattivi</option>
          </select>
        </div>
      </div>

      {/* Error */}
      {error && (
        <div className="bg-red-50 text-red-600 p-4 rounded-lg">{error}</div>
      )}

      {/* Table */}
      <div className="card overflow-hidden">
        {utenti.length > 0 ? (
          <table className="table w-full">
            <thead>
              <tr>
                <th>Nome</th>
                <th>Email</th>
                <th>Ruolo</th>
                <th>RSA/Fornitore</th>
                <th>Stato</th>
                <th>Invito</th>
                <th>Privacy</th>
                <th>Ultimo Accesso</th>
                <th>Azioni</th>
              </tr>
            </thead>
            <tbody className="divide-y divide-gray-200">
              {utenti.map((u) => (
                <tr
                  key={u.id}
                  className={!u.attivo ? "bg-gray-50 opacity-60" : ""}
                >
                  <td className="font-medium">
                    {u.displayName}
                    {u.id === currentUser?.id && (
                      <span className="ml-2 text-xs bg-primary-100 text-primary-700 px-2 py-0.5 rounded">
                        Tu
                      </span>
                    )}
                  </td>
                  <td>{u.email}</td>
                  <td>
                    <span
                      className={`px-2 py-1 rounded text-xs font-medium ${
                        u.ruolo === 100000000
                          ? "bg-purple-100 text-purple-700"
                          : u.ruolo === 100000001
                            ? "bg-blue-100 text-blue-700"
                            : u.ruolo === 100000005
                              ? "bg-orange-100 text-orange-700"
                              : u.ruolo === 100000006
                                ? "bg-teal-100 text-teal-700"
                                : "bg-gray-100 text-gray-700"
                      }`}
                    >
                      {u.ruoloLabel}
                    </span>
                  </td>
                  <td>{u.rsa?.nome || u.fornitore?.nome || "-"}</td>
                  <td>
                    <span
                      className={`px-2 py-1 rounded text-xs ${
                        u.attivo
                          ? "bg-green-100 text-green-700"
                          : "bg-red-100 text-red-700"
                      }`}
                    >
                      {u.attivo ? "Attivo" : "Inattivo"}
                    </span>
                  </td>
                  <td>
                    {u.ultimoAccesso ? (
                      <span className="px-2 py-1 rounded text-xs bg-green-100 text-green-700">
                        Registrato
                      </span>
                    ) : u.invitoStato ? (
                      <span className="px-2 py-1 rounded text-xs bg-yellow-100 text-yellow-700">
                        {u.invitoStato === "inviato"
                          ? "Invito inviato"
                          : "Reinviato"}
                      </span>
                    ) : (
                      <span className="px-2 py-1 rounded text-xs bg-gray-100 text-gray-500">
                        Non invitato
                      </span>
                    )}
                  </td>
                  <td>
                    {u.privacyAccettata ? (
                      <span
                        className="px-2 py-1 rounded text-xs bg-green-100 text-green-700"
                        title={
                          u.dataPrivacyAccettata
                            ? new Date(u.dataPrivacyAccettata).toLocaleString(
                                "it-IT",
                              )
                            : ""
                        }
                      >
                        {u.dataPrivacyAccettata
                          ? new Date(u.dataPrivacyAccettata).toLocaleDateString(
                              "it-IT",
                            )
                          : "Accettata"}
                      </span>
                    ) : (
                      <span className="px-2 py-1 rounded text-xs bg-red-100 text-red-700">
                        Non accettata
                      </span>
                    )}
                  </td>
                  <td className="text-sm text-gray-500">
                    {u.ultimoAccesso
                      ? new Date(u.ultimoAccesso).toLocaleDateString("it-IT")
                      : "Mai"}
                  </td>
                  <td>
                    <div className="flex gap-2 flex-wrap">
                      <button
                        onClick={() => openEditModal(u)}
                        className="text-primary-600 hover:underline text-sm"
                      >
                        Modifica
                      </button>
                      {/* Show resend invite for users who haven't logged in yet */}
                      {!u.ultimoAccesso && ciamConfigured && (
                        <button
                          onClick={() => resendInvite(u)}
                          disabled={resendingInvite === u.id}
                          className="text-blue-600 hover:underline text-sm disabled:opacity-50"
                        >
                          {resendingInvite === u.id
                            ? "Invio..."
                            : "Reinvia invito"}
                        </button>
                      )}
                      <button
                        onClick={() => toggleAttivo(u)}
                        className={`text-sm ${u.attivo ? "text-yellow-600" : "text-green-600"} hover:underline`}
                        disabled={u.id === currentUser?.id}
                      >
                        {u.attivo ? "Disattiva" : "Attiva"}
                      </button>
                      {u.id !== currentUser?.id && (
                        <button
                          onClick={() => deleteUtente(u)}
                          className="text-red-600 hover:underline text-sm"
                        >
                          Elimina
                        </button>
                      )}
                    </div>
                  </td>
                </tr>
              ))}
            </tbody>
          </table>
        ) : (
          <p className="text-gray-500 text-center py-8">
            Nessun utente trovato
          </p>
        )}
      </div>

      {/* Modal */}
      {showModal && (
        <div className="fixed inset-0 bg-black/50 flex items-center justify-center z-50">
          <div className="bg-white rounded-lg shadow-xl w-full max-w-lg mx-4">
            <div className="p-6 border-b">
              <h2 className="text-xl font-semibold">
                {modalMode === "create" ? "Nuovo Utente" : "Modifica Utente"}
              </h2>
            </div>

            <form onSubmit={handleSubmit}>
              <div className="p-6 space-y-4">
                {formError && (
                  <div className="bg-red-50 text-red-600 p-3 rounded text-sm">
                    {formError}
                  </div>
                )}

                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-1">
                    Nome Visualizzato *
                  </label>
                  <input
                    type="text"
                    value={formData.displayName}
                    onChange={(e) =>
                      setFormData({ ...formData, displayName: e.target.value })
                    }
                    className="input w-full"
                    required
                  />
                </div>

                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-1">
                    Email *
                  </label>
                  <input
                    type="email"
                    value={formData.email}
                    onChange={(e) =>
                      setFormData({ ...formData, email: e.target.value })
                    }
                    className="input w-full"
                    required
                    disabled={modalMode === "edit"}
                  />
                </div>

                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-1">
                    Ruolo *
                  </label>
                  <select
                    value={formData.ruolo}
                    onChange={(e) =>
                      setFormData({ ...formData, ruolo: e.target.value })
                    }
                    className="input w-full"
                    required
                  >
                    <option value="">Seleziona ruolo</option>
                    {RUOLI.map((r) => (
                      <option key={r.value} value={r.value}>
                        {r.label}
                      </option>
                    ))}
                  </select>
                </div>

                {/* RSA - shown for Direttore RSA and Amministrativo */}
                {[100000003, 100000004].includes(parseInt(formData.ruolo)) && (
                  <div>
                    <label className="block text-sm font-medium text-gray-700 mb-1">
                      RSA di Riferimento *
                    </label>
                    <select
                      value={formData.rsaId}
                      onChange={(e) =>
                        setFormData({ ...formData, rsaId: e.target.value })
                      }
                      className="input w-full"
                      required
                    >
                      <option value="">Seleziona RSA</option>
                      {rsaList.map((r) => (
                        <option key={r.id} value={r.id}>
                          {r.nome}
                        </option>
                      ))}
                    </select>
                  </div>
                )}

                {/* Fornitore - shown for Fornitore role */}
                {parseInt(formData.ruolo) === 100000005 && (
                  <div>
                    <label className="block text-sm font-medium text-gray-700 mb-1">
                      Fornitore *
                    </label>
                    <select
                      value={formData.fornitoreId}
                      onChange={(e) =>
                        setFormData({
                          ...formData,
                          fornitoreId: e.target.value,
                        })
                      }
                      className="input w-full"
                      required
                    >
                      <option value="">Seleziona Fornitore</option>
                      {fornitoriList.map((f) => (
                        <option key={f.id} value={f.id}>
                          {f.nome}
                        </option>
                      ))}
                    </select>
                  </div>
                )}

                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-1">
                    Note
                  </label>
                  <textarea
                    value={formData.note}
                    onChange={(e) =>
                      setFormData({ ...formData, note: e.target.value })
                    }
                    className="input w-full"
                    rows={3}
                  />
                </div>
              </div>

              <div className="p-6 border-t bg-gray-50 flex justify-end gap-3">
                <button
                  type="button"
                  onClick={() => setShowModal(false)}
                  className="btn-secondary"
                  disabled={submitting}
                >
                  Annulla
                </button>
                <button
                  type="submit"
                  className="btn-primary"
                  disabled={submitting}
                >
                  {submitting
                    ? "Salvataggio..."
                    : modalMode === "create"
                      ? "Crea Utente"
                      : "Salva Modifiche"}
                </button>
              </div>
            </form>
          </div>
        </div>
      )}
    </div>
  );
}

export default UtentiPage;
